/**
 * D-138: Website → Local pages. One page « Préparer le TCF Canada à <ville> » per city of the
 * versioned data. The facts (exam centres and their status, time zone, payment methods, the
 * agency's office) are read by the site from the data; here the agency writes its own texts for
 * each city and chooses whether the page is online.
 */

import {
  getWebsiteLocalPagesListQueryKey,
  type LocalPage,
  useWebsiteLocalPagesList,
  useWebsiteLocalPagesPartialUpdate,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, SegmentedControl, Skeleton, SoftToggle, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors, useCanEditSettings } from "../../lib/settings";
import { MarkdownEditor } from "./MarkdownEditor";
import "./blog.css";

type Lang = "fr" | "en";
interface CityTexts {
  intro: string;
  body: string;
  seo_description: string;
}
type Content = Record<Lang, CityTexts>;

function contentOf(row: LocalPage): Content {
  const raw = (row.content ?? {}) as Record<string, Partial<CityTexts> | undefined>;
  const pick = (lang: Lang): CityTexts => ({
    intro: raw[lang]?.intro ?? "",
    body: raw[lang]?.body ?? "",
    seo_description: raw[lang]?.seo_description ?? "",
  });
  return { fr: pick("fr"), en: pick("en") };
}

function siteOrigin(): string {
  const url = new URL(window.location.origin);
  if (url.port === "5173") url.port = "8001";
  return url.origin;
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: getWebsiteLocalPagesListQueryKey() });
}

function CityEditor({ row, onClose }: { row: LocalPage; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const canEdit = useCanEditSettings();
  const refresh = useRefresh();
  const update = useWebsiteLocalPagesPartialUpdate({ mutation: { onSuccess: refresh } });
  const [content, setContent] = useState<Content>(() => contentOf(row));
  const [lang, setLang] = useState<Lang>(i18n.language === "en" ? "en" : "fr");
  const [saved, setSaved] = useState(false);
  const errors = fieldErrors(update.error);
  const set = (key: keyof CityTexts, value: string) => {
    setSaved(false);
    setContent((c) => ({ ...c, [lang]: { ...c[lang], [key]: value } }));
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    update.mutate({ id: row.id, data: { content } }, { onSuccess: (r) => setSaved(r.status === 200) });
  };
  return (
    <form className="stack blog-editor" onSubmit={onSubmit} noValidate>
      <div className="row blog-editor__top">
        <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden="true" />} onClick={onClose}>
          {t("localPages.back")}
        </Button>
        <strong>{row.city_name}</strong>
        <a className="link" href={`${siteOrigin()}${row.urls[lang] ?? ""}`} target="_blank" rel="noreferrer">
          <ExternalLink size={16} aria-hidden="true" /> {t("website.viewPage")}
        </a>
      </div>
      <GlassCard as="section" className="stack">
        <p className="muted">{t("localPages.factsHint")}</p>
        <SegmentedControl label={t("blog.editor.language")} value={lang} onChange={setLang} options={(["fr", "en"] as Lang[]).map((l) => ({ value: l, label: t(`catalog.language.${l}`) }))} />
        <TextField label={t("localPages.intro")} lang={lang} value={content[lang].intro} maxLength={400} disabled={!canEdit} onChange={(e) => set("intro", e.target.value)} />
        <MarkdownEditor label={t("localPages.body")} lang={lang} value={content[lang].body} disabled={!canEdit} error={errors.content} rows={14} onChange={(v) => set("body", v)} />
        <TextField label={t("blog.field.seoDescription")} hint={t("blog.editor.counter", { n: content[lang].seo_description.length, max: 155 })} lang={lang} value={content[lang].seo_description} maxLength={170} disabled={!canEdit} onChange={(e) => set("seo_description", e.target.value)} />
        {canEdit ? (
          <div className="row">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? t("common.loading") : t("common.save")}
            </Button>
            {saved ? (
              <span role="status" className="muted">
                {t("website.savedOnline")}
              </span>
            ) : null}
          </div>
        ) : null}
      </GlassCard>
    </form>
  );
}

export function LocalPagesTab() {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const refresh = useRefresh();
  const list = useWebsiteLocalPagesList();
  const update = useWebsiteLocalPagesPartialUpdate({ mutation: { onSuccess: refresh } });
  const [editing, setEditing] = useState<LocalPage | null>(null);
  const rows = list.data?.status === 200 ? list.data.data : [];
  if (editing) return <CityEditor key={editing.id} row={editing} onClose={() => setEditing(null)} />;
  return (
    <GlassCard as="section" className="stack">
      <div>
        <h2>{t("localPages.title")}</h2>
        <p className="muted">{t("localPages.intro_text")}</p>
      </div>
      {list.isPending ? (
        <Skeleton style={{ height: 200 }} />
      ) : rows.length ? (
        <ul className="local-list">
          {rows.map((row) => (
            <li key={row.id} className="local-list__row">
              <div className="stack-xs">
                <button type="button" className="link" onClick={() => setEditing(row)}>
                  {t("localPages.pageTitle", { city: row.city_name })}
                </button>
                <span className="muted ao-num">
                  {row.country} · {row.timezone}
                </span>
              </div>
              <Badge tone={row.published ? "success" : "default"} dot>
                {row.published ? t("localPages.online") : t("localPages.offline")}
              </Badge>
              <SoftToggle
                label={t("localPages.publish", { city: row.city_name })}
                checked={Boolean(row.published)}
                disabled={!canEdit || update.isPending}
                onChange={(published) => update.mutate({ id: row.id, data: { published } })}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t("localPages.empty")}</p>
      )}
    </GlassCard>
  );
}
